import { useEffect, useMemo, useState } from "react";
import {
  buildQuickOptions,
  minRedeemablePoints,
  pointsToAmount,
} from "../../../utils/loyalty/payWithPoints";
import type { PayWithPointsQuickOption } from "../../../utils/loyalty/payWithPoints";
import { getCookie } from "../../../utils/CookieHelper";
import { COOKIE_ENUM } from "../../../utils/enums";
import { parseJwt } from "../../../utils/help/user/user";
import { LoyaltyMechanism } from "../../../utils/loyaltyTypes";
import { selectGuestTiersEnabled } from "../../redux/selectors/loyaltyServiceSelectors";
import {
  selectIsPayWithPointsApplied,
  selectPayWithPointsApplied,
  selectPayWithPointsMaximum,
  selectPayWithPointsReduction,
  selectPayWithPointsRequest,
} from "../../redux/selectors/payWithPointsSelectors";
import { useAppSelector } from "../useReduxHooks";
import { useTranslationI18 } from "../useTranslationI18";
import { useLoyaltyUiModel } from "../useLoyaltyUiModel";

export enum PayWithPointsDisabledReason {
  NOT_SIGNED_IN = "NOT_SIGNED_IN",
  BELOW_MINIMUM = "BELOW_MINIMUM",
  NO_CART_BALANCE = "NO_CART_BALANCE",
}

export type PayWithPointsSurface = "cart" | "browse";

export const usePayWithPoints = (surface: PayWithPointsSurface = "cart") => {
  const { t, i18n } = useTranslationI18();
  const pointsText = t("pointing-system:point");
  const { displayPoints, currentTier, pointName, useLoyaltyService } =
    useLoyaltyUiModel(pointsText, i18n);

  const reduxToken = useAppSelector((state) => state.userInfo?.token);
  const phoneNumberInfo = useAppSelector(
    (state) => state.userInfo?.phoneNumberInfo
  );

  // ----------------------Read on mount, never during render: parseJwt needs window, and SSR would strand the signed-out card-----------------------
  const [cookieToken, setCookieToken] = useState<string | undefined>(undefined);
  useEffect(() => {
    setCookieToken(getCookie(COOKIE_ENUM.TOKEN));
  }, []);

  const isSignedIn = useMemo(() => {
    const token = reduxToken ?? cookieToken;
    const payload = token ? parseJwt(token) : undefined;
    if (payload) {
      return !payload[COOKIE_ENUM.VISITOR];
    }

    return !!phoneNumberInfo?.isValid;
  }, [reduxToken, cookieToken, phoneNumberInfo?.isValid]);
  const loyaltyCustomerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo?.data
  );
  const mechanism = useAppSelector(
    (state) => state.loyaltyTierSystem?.data?.mechanism
  );
  const hasTiers = useAppSelector(selectGuestTiersEnabled);

  const request = useAppSelector(selectPayWithPointsRequest);
  const applied = useAppSelector(selectPayWithPointsApplied);
  const maximum = useAppSelector(selectPayWithPointsMaximum);
  const isApplied = useAppSelector(selectIsPayWithPointsApplied);
  const reduction = useAppSelector(selectPayWithPointsReduction);

  const redemptionRate =
    currentTier?.redemptionRate ?? loyaltyCustomerInfo?.redemptionRate ?? 0;
  const redemptionIncrement = currentTier?.redemptionIncrement ?? 1;

  const isPayWithPointsMechanism =
    mechanism === LoyaltyMechanism.PAY_WITH_POINTS ||
    mechanism === LoyaltyMechanism.PAY_WITH_POINTS_AND_TIERS;

  const isEnabled =
    useLoyaltyService && isPayWithPointsMechanism && redemptionRate > 0;

  const maxPoints = maximum?.points ?? 0;
  const maxAmount = maximum?.amount ?? 0;

  const balancePoints = isSignedIn ? displayPoints : 0;
  const balanceAmount = pointsToAmount(balancePoints, redemptionRate);

  // ----------------------On the cart the server's per-order cap is what can actually be spent, so quoting the whole balance overstates it; browse has no order yet, so the balance stands in-----------------------
  const spendablePoints = surface === "cart" ? maxPoints : balancePoints;
  const spendableAmount = surface === "cart" ? maxAmount : balanceAmount;
  const minPoints = minRedeemablePoints(redemptionIncrement);

  const disabledReason: PayWithPointsDisabledReason | undefined =
    useMemo(() => {
      if (!isSignedIn) {
        return PayWithPointsDisabledReason.NOT_SIGNED_IN;
      }

      if (balancePoints < minPoints || balanceAmount <= 0) {
        return PayWithPointsDisabledReason.BELOW_MINIMUM;
      }

      if (surface === "cart" && maxPoints <= 0) {
        return PayWithPointsDisabledReason.NO_CART_BALANCE;
      }

      return undefined;
    }, [
      surface,
      isSignedIn,
      balancePoints,
      balanceAmount,
      minPoints,
      maxPoints,
    ]);

  const quickOptions: PayWithPointsQuickOption[] = useMemo(
    () =>
      buildQuickOptions(
        maxPoints,
        maxAmount,
        redemptionRate,
        redemptionIncrement
      ),
    [maxPoints, maxAmount, redemptionRate, redemptionIncrement]
  );

  const expiringThisMonth = useMemo(() => {
    const expiry = loyaltyCustomerInfo?.pointsExpiry;

    if (!expiry?.amount || !expiry.expiresAt) {
      return undefined;
    }

    const expiresAt = new Date(expiry.expiresAt);
    const now = new Date();
    const sameMonth =
      expiresAt.getFullYear() === now.getFullYear() &&
      expiresAt.getMonth() === now.getMonth();

    return sameMonth ? expiry.amount : undefined;
  }, [loyaltyCustomerInfo?.pointsExpiry]);

  return {
    isEnabled,
    isPayWithPointsMechanism,
    hasTiers,
    isSignedIn,
    disabledReason,
    isRedeemable: isEnabled && disabledReason === undefined,
    isApplied,
    requestedPoints: request?.requestedPoints,
    appliedPoints: applied?.points ?? 0,
    appliedAmount: applied?.amount ?? 0,
    maxPoints,
    maxAmount,
    minPoints,
    balancePoints,
    balanceAmount,
    spendablePoints,
    spendableAmount,
    redemptionRate,
    redemptionIncrement,
    quickOptions,
    reduction,
    currentTier,
    pointName,
    expiringPoints: expiringThisMonth,
  };
};
